import styled from 'styled-components';
import { MultiLineInput } from '../elements/Input';
import React, { useState, useRef, useEffect } from 'react';
import { P, PExtraSmall, PSmall } from '../elements/Text';
import Button from '../elements/Button';
import { Tooltip } from 'react-tooltip';
import Markdown from '../Markdown';
import Loader from '../Loader/LoaderDashedLines';

const Wrapper = styled.div`
  flex: 1;
  display: flex;
  height: 100%;
  background: ${(props) => props.theme.colors.bgDefault};
  border-left: 1px solid ${(props) => props.theme.colors.zinc800};
  flex-direction: column;
  overflow-y: auto;
  padding: 5px 10px;
`;

const Section = styled.div<{ isFaded?: boolean }>`
  padding: 0px;
  margin: 6px 0;
  border-radius: 8px;
  border: 1px solid ${(props) => props.theme.colors.divider};
  background: ${(props) => props.theme.colors.bgDefaultSecondary};
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  transition: opacity 0.3s ease, box-shadow 0.3s ease, transform 0.3s ease;
  opacity: ${(props) => (props.isFaded ? 0.4 : 1)};
  overflow-y: auto;

  ${(props) =>
    props.isFaded &&
    `
    box-shadow: none;
  `}
`;

const SectionTitle = styled(PSmall)`
  margin: 0;
  font-weight: 500;
  color: ${(props) => props.theme.colors.textSecondary};
  font-size: 13px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 5px;
  display: flex;
  align-items: center;
  gap: 6px;

  i {
    font-size: 14px;
  }
`;

const PromptSection = styled(Section)<{
  isExpanded: boolean;
  shouldShowReview: boolean;
  isEditOnly: boolean;
}>`
  height: ${(props) =>
    !props.shouldShowReview ? '60%' : props.isExpanded ? '40%' : '25%'};
  ${({ isEditOnly }) =>
    isEditOnly &&
    `
      height: 100%;
    `}

  position: relative;
  display: flex;
  flex-direction: column;
  transition: height 0.3s ease-in-out;
  border-right: 0;
`;

const OutputSection = styled(Section)<{
  isExpanded: boolean;
  shouldShowReview: boolean;
}>`
  height: ${(props) =>
    !props.shouldShowReview ? '40%' : props.isExpanded ? '40%' : '50%'};
  transition: height 0.3s ease;
`;

const ReviewSection = styled(Section)<{ isExpanded: boolean }>`
  height: ${(props) => (props.isExpanded ? '20%' : '25%')};
  padding: 12px 20px;
  transition: height 0.3s ease;
`;

const MarkdownWrapper = styled.div`
  overflow-y: auto;
  height: 100%;
  display: flex;
  flex-direction: column;
`;

const ValidationErrorLabel = styled(PSmall)`
  color: ${({ theme }) => theme.colors.textDanger};
`;

const InitialStateMessage = styled(PSmall)`
  color: ${({ theme }) => theme.colors.textRegular};
  text-align: center;
  padding: 20px;
  line-height: 1.5;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  min-height: 100px;
  margin: 0;
`;

const CompactMarkdownComponents = {
  h1: ({ ...props }) => (
    <P {...props} style={{ fontSize: '16px', margin: '8px 0' }} />
  ),
  h2: ({ ...props }) => (
    <P {...props} style={{ fontSize: '15px', margin: '6px 0' }} />
  ),
  h3: ({ ...props }) => (
    <P {...props} style={{ fontSize: '14px', margin: '4px 0' }} />
  ),
  h4: ({ ...props }) => (
    <P {...props} style={{ fontSize: '14px', margin: '2px 0' }} />
  ),
  h5: ({ ...props }) => (
    <P {...props} style={{ fontSize: '14px', margin: '2px 0' }} />
  ),
  h6: ({ ...props }) => (
    <P {...props} style={{ fontSize: '14px', margin: '2px 0' }} />
  ),
  p: ({ ...props }) => <PSmall {...props} style={{ margin: '4px 0' }} />,
  ul: ({ ...props }) => (
    <ul {...props} style={{ margin: '4px', paddingLeft: '16px' }} />
  ),
  ol: ({ ...props }) => (
    <ol {...props} style={{ margin: '4px', paddingLeft: '16px' }} />
  ),
  li: ({ ...props }) => (
    <li
      {...props}
      style={{ fontSize: '14px', margin: '2px 0', lineHeight: '1.4' }}
    />
  ),
  a: ({ ...props }) => <a {...props} style={{ fontSize: '14px' }} />,
  code: ({ ...props }) => <code {...props} style={{ fontSize: '14px' }} />,
};

export default function LLMPlayground({
  userPrompt,
  onPromptChange,
  result,
  review,
  onSubmitPrompt,
  isLoading,
  onReset,
  validationError,
  shouldShowReview = true,
  shouldRunInsteadOfSubmit = false,
  onPaste,
  isEditOnly = false,
}: {
  userPrompt: string;
  onPromptChange: (prompt: string) => void;
  result: string | null;
  review: string | null;
  onSubmitPrompt: () => void;
  onReset: () => void;
  isLoading: boolean;
  validationError: string | null;
  shouldShowReview?: boolean;
  shouldRunInsteadOfSubmit?: boolean;
  onPaste?: (e: React.ClipboardEvent<HTMLTextAreaElement>) => void;
  isEditOnly?: boolean;
}) {
  const [isPromptFocused, setIsPromptFocused] = useState(false);
  const [isPromptHovered, setIsPromptHovered] = useState(false);
  const [lastSubmittedPrompt, setLastSubmittedPrompt] =
    useState<string | null>(null);
  const promptRef = useRef<HTMLDivElement>(null);

  const hasOutput = !!result || !!review;
  const isPromptExpanded = hasOutput
    ? isPromptFocused || isPromptHovered
    : true;
  const isPromptModified =
    lastSubmittedPrompt !== null &&
    userPrompt.trim() !== lastSubmittedPrompt.trim();

  // Handle click outside to unfocus prompt
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        promptRef.current &&
        !promptRef.current.contains(event.target as Node)
      ) {
        setIsPromptFocused(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <Wrapper className="llm-playground-wrapper">
      <SectionTitle>
        <i className="fa-solid fa-keyboard"></i>Prompt
      </SectionTitle>
      <PromptSection
        ref={promptRef}
        isExpanded={isPromptExpanded}
        shouldShowReview={shouldShowReview}
        onMouseEnter={() => setIsPromptHovered(true)}
        onMouseLeave={() => setIsPromptHovered(false)}
        onClick={() => setIsPromptFocused(true)}
        isEditOnly={isEditOnly}
      >
        <PromptEditor
          userPrompt={userPrompt}
          onPromptChange={onPromptChange}
          setPromptFocused={setIsPromptFocused}
          isExpanded={isPromptExpanded}
          isLoading={isLoading}
          onPaste={onPaste}
          shouldShowReview={shouldShowReview}
        />
        <Controls
          hasPrompt={!!userPrompt}
          onSubmitPrompt={() => {
            setLastSubmittedPrompt(userPrompt);
            onSubmitPrompt();
          }}
          onReset={onReset}
          isLoading={isLoading}
          isPromptFocused={isPromptFocused}
          isPromptModified={isPromptModified}
          hasOutput={hasOutput}
          setPromptFocused={setIsPromptFocused}
          shouldRunInsteadOfSubmit={shouldRunInsteadOfSubmit}
          isEditOnly={isEditOnly}
        />
      </PromptSection>
      {!isEditOnly && (
        <>
          <SectionTitle>
            <i className="fa-solid fa-robot"></i>AI Reply
          </SectionTitle>
          <OutputSection
            isFaded={isPromptModified && !!result}
            isExpanded={isPromptExpanded}
            shouldShowReview={shouldShowReview}
          >
            <MarkdownWrapper style={{ padding: '12px 20px' }}>
              {validationError && (
                <ValidationErrorLabel>{validationError}</ValidationErrorLabel>
              )}
              {!validationError && (
                <>
                  {isLoading && !result ? (
                    <HorizontalBarLoader label="Generating AI reply..." />
                  ) : result ? (
                    <Markdown
                      md={result}
                      components={CompactMarkdownComponents}
                    />
                  ) : (
                    <InitialStateMessage>
                      Write a prompt and submit to see the AI's reply here.
                    </InitialStateMessage>
                  )}
                </>
              )}
            </MarkdownWrapper>
          </OutputSection>

          {shouldShowReview && (
            <>
              <SectionTitle>
                <i className="fa-solid fa-comment-dots"></i>AI Mentor Review
              </SectionTitle>
              <ReviewSection
                isFaded={isPromptModified && !!review}
                isExpanded={isPromptExpanded}
              >
                <MarkdownWrapper>
                  {isLoading && !review ? (
                    <HorizontalBarLoader
                      label={
                        result
                          ? 'Reviewing your submission...'
                          : 'Waiting for AI reply before reviewing...'
                      }
                    />
                  ) : review ? (
                    <Markdown
                      md={review}
                      components={CompactMarkdownComponents}
                    />
                  ) : (
                    <InitialStateMessage>
                      After submitting your prompt, Enki's AI mentor will review
                      your submission here.
                    </InitialStateMessage>
                  )}
                </MarkdownWrapper>
              </ReviewSection>
            </>
          )}
        </>
      )}
    </Wrapper>
  );
}

const PromptEditorWrapper = styled.div.attrs<{
  isExpanded: boolean;
  shouldShowReview: boolean;
}>((props) => ({
  isExpanded: props.isExpanded,
}))<{ isExpanded: boolean; shouldShowReview: boolean }>`
  background: ${(props) => props.theme.colors.bgDefault};
  flex: 1;
  overflow-y: auto;
  max-height: ${(props) =>
    !props.shouldShowReview ? 'none' : props.isExpanded ? 'none' : '110px'};
  position: relative;
  transition: max-height 0.3s cubic-bezier(0.4, 0, 0.2, 1);
`;

const PromptInput = styled(MultiLineInput)`
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
  padding-bottom: 0;
  margin-top: 0px;

  div {
    background: transparent;
    border: none;
    padding: 0;

    textarea {
      padding: 10px 20px;
      font-size: 14px;
      &::placeholder {
        color: ${({ theme }) => theme.colors.textPlaceholder};
      }
    }
  }
`;

const ControlsRow = styled.div`
  display: flex;
  flex-direction: row;
  padding: 5px 20px;
  justify-content: space-between;
  border-top: 1px solid ${(props) => props.theme.colors.divider};
  flex-shrink: 0;
`;

const ControlsGroup = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
  align-items: center;
  justify-content: center;
`;

const ControlsButton = styled(Button)`
  margin-top: 0px;
  padding: 12px;
  p {
    font-size: 12px !important;
  }
  svg {
    width: 16px;
  }
  line-height: 16px;
`;

function PromptEditor({
  userPrompt,
  onPromptChange,
  setPromptFocused,
  isExpanded,
  isLoading,
  onPaste,
  shouldShowReview,
}: {
  userPrompt: string;
  onPromptChange: (prompt: string) => void;
  setPromptFocused: (focused: boolean) => void;
  isExpanded: boolean;
  isLoading: boolean;
  onPaste?: (e: React.ClipboardEvent<HTMLTextAreaElement>) => void;
  shouldShowReview: boolean;
}) {
  const handleContainerClick = () => {
    setPromptFocused(true);
  };

  return (
    <PromptEditorWrapper
      className="llm-playground-prompt-input-wrapper"
      isExpanded={isExpanded}
      shouldShowReview={shouldShowReview}
      onClick={handleContainerClick}
    >
      <PromptInput
        className="llm-playground-prompt-input"
        value={userPrompt}
        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
          onPromptChange(e.target.value)
        }
        placeholder={`Write your prompt here.`}
        minRows={5}
        isDisabled={isLoading}
        {...(onPaste ? { onPaste } : {})}
      />
    </PromptEditorWrapper>
  );
}

const Icon = styled.i<{
  isDisabled?: boolean;
}>`
  color: ${(props) => props.theme.colors.textRegular};
  cursor: pointer;

  ${({ isDisabled }) =>
    isDisabled &&
    `
      opacity: 0.5;
      cursor: not-allowed;
      pointer-events: none;
    `}
`;

const ModelLabel = styled(PExtraSmall)`
  margin: 0;
  padding: 0;
  color: ${(props) => props.theme.colors.textPlaceholder};
`;

function Controls({
  shouldRunInsteadOfSubmit,
  hasPrompt,
  onSubmitPrompt,
  onReset,
  isLoading,
  isPromptFocused,
  isPromptModified,
  hasOutput,
  setPromptFocused,
  isEditOnly,
}: {
  shouldRunInsteadOfSubmit: boolean;
  hasPrompt: boolean;
  onSubmitPrompt: () => void;
  onReset: () => void;
  isLoading: boolean;
  isPromptFocused: boolean;
  isPromptModified: boolean;
  hasOutput: boolean;
  setPromptFocused: (focused: boolean) => void;
  isEditOnly: boolean;
}) {
  return (
    <ControlsRow className="llm-playground-controls">
      <ControlsGroup>
        <>
          <Icon
            className={`fa-solid fa-rotate-left playground-icon-reset`}
            onClick={onReset}
          />
          <Tooltip noArrow anchorSelect=".playground-icon-reset" place="top">
            Start over
          </Tooltip>
        </>
        <ModelLabel>Model: GPT-4o-mini</ModelLabel>
      </ControlsGroup>
      {!isEditOnly && (
        <ControlsGroup>
          <ControlsButton
            label={shouldRunInsteadOfSubmit ? 'Run' : 'Submit'}
            type="primary"
            isLoading={isLoading}
            tooltip={
              isPromptFocused
                ? hasOutput && isPromptModified
                  ? 'Changing the prompt will change the output and review'
                  : 'Generate AI output for your prompt'
                : hasPrompt
                ? undefined
                : 'You must write a prompt before you can submit :)'
            }
            faIcon={
              shouldRunInsteadOfSubmit
                ? 'fa-solid fa-play-circle'
                : 'fa-solid fa-paper-plane'
            }
            showIconBeforeLabel
            onClick={() => {
              setPromptFocused(false);
              onSubmitPrompt();
            }}
            isDisabled={isLoading || !hasPrompt}
          />
        </ControlsGroup>
      )}
    </ControlsRow>
  );
}

const LoaderWrapper = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

function HorizontalBarLoader({ label }: { label: string }) {
  return (
    <LoaderWrapper>
      <Loader title={label} size={100} />
    </LoaderWrapper>
  );
}
